import type { ReactNode } from 'react';
import { type ExternalToast, toast as sonnerToast } from 'sonner';

/**
 * Toast API shim over sonner. Preserves the object-shaped `toast({ title, description, variant })`
 * call signature used across the apps (and the error-handling pattern in CLAUDE.md), so callers
 * did not change when we migrated off the Radix toast. Each variant maps to sonner's matching
 * method, which renders a colored leading icon; `default` is the neutral toast.
 */
type ToastVariant = 'default' | 'destructive' | 'success' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastVariant;
  /** Single action button rendered on the toast (e.g. "Undo"). */
  action?: ToastAction;
}

function toast({ title, description, variant, action }: ToastOptions) {
  const message = title ?? description ?? '';
  const options: ExternalToast = {};
  if (title && description) {
    options.description = description;
  }
  if (action) {
    options.action = { label: action.label, onClick: action.onClick };
  }

  switch (variant) {
    case 'destructive':
      return sonnerToast.error(message, options);
    case 'success':
      return sonnerToast.success(message, options);
    case 'warning':
      return sonnerToast.warning(message, options);
    case 'info':
      return sonnerToast.info(message, options);
    default:
      return sonnerToast(message, options);
  }
}

function useToast() {
  return { toast, dismiss: sonnerToast.dismiss };
}

export { toast, useToast };
